/**
 * Ориентация снимка из EXIF.
 *
 * Камера почти всегда пишет кадр в исходной ориентации матрицы и помечает поворот
 * тегом. Без этого страницы дела ложатся боком — поймано на Android (Galaxy S8):
 * все снятые страницы оказывались повёрнутыми, и это было первым, что заметил юрист.
 *
 * Свой разбор, а не библиотека: нужен ровно один тег из заголовка, а любая библиотека
 * EXIF тянет разбор GPS, миниатюр и производителей — в офлайн-первое приложение это
 * кладётся целиком.
 */

/** 1 — снимок стоит правильно; так же трактуется всё, чего мы не поняли. */
export const ORIENTATION_NORMAL = 1;

const JPEG_START = 0xffd8;
const APP1 = 0xffe1;
const EXIF_HEADER = 0x45786966; // «Exif»
const LITTLE_ENDIAN = 0x4949; // «II»
const BIG_ENDIAN = 0x4d4d; // «MM»
const TAG_ORIENTATION = 0x0112;
const IFD_ENTRY_SIZE = 12;
const MAX_ORIENTATION = 8;

/**
 * Читает тег ориентации. Всё, что не JPEG с EXIF, — «поворачивать нечего».
 *
 * Ошибок не выбрасывает намеренно: испорченный заголовок не повод не класть документ
 * в дело. Снимок ляжет как есть, и это лучше, чем потерянная страница.
 */
export function orientationOf(bytes: ArrayBuffer): number {
  const view = new DataView(bytes);
  if (view.byteLength < 4 || view.getUint16(0) !== JPEG_START) return ORIENTATION_NORMAL;

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset);
    const size = view.getUint16(offset + 2);
    if (marker === APP1) {
      const found = readOrientation(view, offset + 4, size - 2);
      return found ?? ORIENTATION_NORMAL;
    }

    // Не тот сегмент — перешагиваем через него целиком.
    if ((marker & 0xff00) !== 0xff00 || size < 2) return ORIENTATION_NORMAL;
    offset += 2 + size;
  }
  return ORIENTATION_NORMAL;
}

function readOrientation(view: DataView, start: number, length: number): number | null {
  const MIN_APP1 = 14;
  if (length < MIN_APP1 || start + length > view.byteLength) return null;
  if (view.getUint32(start) !== EXIF_HEADER) return null;

  // «Exif\0\0», дальше TIFF-заголовок, от которого считаются все смещения.
  const tiff = start + 6;
  const endian = view.getUint16(tiff);
  if (endian !== LITTLE_ENDIAN && endian !== BIG_ENDIAN) return null;

  const little = endian === LITTLE_ENDIAN;
  const ifd = tiff + view.getUint32(tiff + 4, little);
  if (ifd + 2 > view.byteLength) return null;

  const count = view.getUint16(ifd, little);
  for (let index = 0; index < count; index += 1) {
    const entry = ifd + 2 + index * IFD_ENTRY_SIZE;
    if (entry + IFD_ENTRY_SIZE > view.byteLength) return null;

    if (view.getUint16(entry, little) === TAG_ORIENTATION) {
      const value = view.getUint16(entry + 8, little);
      return value >= ORIENTATION_NORMAL && value <= MAX_ORIENTATION ? value : null;
    }
  }
  return null;
}

/** Поворот в градусах и отражение, которые вернут снимок в вид «как видел юрист». */
export function transformFor(orientation: number): { rotate: number; mirror: boolean } {
  const transforms: Record<number, { rotate: number; mirror: boolean }> = {
    1: { rotate: 0, mirror: false },
    2: { rotate: 0, mirror: true },
    3: { rotate: 180, mirror: false },
    4: { rotate: 180, mirror: true },
    5: { rotate: 90, mirror: true },
    6: { rotate: 90, mirror: false },
    7: { rotate: 270, mirror: true },
    8: { rotate: 270, mirror: false },
  };

  return transforms[orientation] ?? { rotate: 0, mirror: false };
}

/**
 * Размер кадра, записанный в самом JPEG (маркер SOF).
 *
 * Нужен, чтобы понять, развернул ли снимок браузер: если декодированный кадр
 * оказался «повёрнут» относительно этого размера, EXIF уже применён — и наш поворот
 * стал бы вторым. См. `photoPdf.ts`.
 */
export function sizeOf(bytes: ArrayBuffer): { width: number; height: number } | null {
  const view = new DataView(bytes);
  if (view.byteLength < 4 || view.getUint16(0) !== JPEG_START) return null;

  let offset = 2;
  while (offset + 9 <= view.byteLength) {
    const marker = view.getUint16(offset);
    if ((marker & 0xff00) !== 0xff00) return null;

    // SOF0…SOF15 несут размер кадра; SOF4, SOF8 и SOF12 — не кадры, а таблицы.
    const isFrame =
      marker >= 0xffc0 && marker <= 0xffcf && ![0xffc4, 0xffc8, 0xffcc].includes(marker);
    if (isFrame) {
      return { height: view.getUint16(offset + 5), width: view.getUint16(offset + 7) };
    }

    const size = view.getUint16(offset + 2);
    if (size < 2) return null;
    offset += 2 + size;
  }
  return null;
}
